const initials = ['B', 'KB', 'MB', 'GB', 'TB'];

/**
 * Normaliza o tamanho de arquivo que a API devolve, que NÃO tem unidade fixa.
 *
 * O campo se chama `SizeInBytes` (e `FileInfo.Size`), mas a unidade muda com a
 * API de armazenamento do arquivo. Dois exemplos reais do mesmo endpoint:
 *
 *   Google Drive (Api 3): 34.692898  → megabytes
 *   Dropbox      (Api 2): 539834     → bytes
 *   OneDrive     (Api 5): 539834     → bytes
 *
 * O Google Drive é a origem fora do padrão; as demais observadas devolvem bytes.
 *
 * Como separar: **contagem de bytes é sempre inteira**. Valor com parte
 * fracionária é megabyte. E um inteiro pequeno também é megabyte — não existe
 * modelo de 800 bytes, mas existe de 800 MB, e um valor em MB pode calhar de ser
 * redondo (exatamente 12 MB vira `12`).
 *
 * Isto é remendo de frontend: o certo é o backend devolver uma unidade só. Até
 * lá, a heurística fica em UM lugar, para as telas não divergirem entre si —
 * hoje a listagem mostra "34.7B" e o viewer federado mostra "0.0 MB" para o
 * mesmo arquivo, cada uma errando de um jeito.
 */
/**
 * `ApisEnum.GoogleDrive`. Repetido como literal em vez de importado porque
 * `Data/enums/Apis` puxa ícones e imagens junto, e este utilitário não pode
 * arrastar a camada de UI.
 */
const API_GOOGLE_DRIVE = 3;

export const fileSizeToBytes = (size?: number | null, api?: number | null): number => {
  const value = Number(size);

  if (!Number.isFinite(value) || value <= 0) { return 0; }

  // Quando a origem é conhecida, não se depende da heurística: um modelo de
  // 1.500 MB no Drive chega como inteiro grande e seria lido como bytes.
  if (api === API_GOOGLE_DRIVE) { return value * 1024 * 1024; }

  const isProbablyMegabytes = !Number.isInteger(value) || value < 1024;

  return isProbablyMegabytes ? value * 1024 * 1024 : value;
};

interface IFileSizeSource {
  SizeInBytes?: number | null;
  FileInfo?: { Size?: number | null; Api?: number | null } | null;
}

/**
 * Tamanho de um item da listagem. `FileInfo.Size` antes de `SizeInBytes`: no
 * Google Drive o `SizeInBytes` já pode vir em bytes enquanto o `FileInfo.Size`
 * vem em MB, e o `api === 3` acima leria os bytes como MB (1.8 MB → 1.8 TB).
 */
export const fileDataSizeToBytes = (file?: IFileSizeSource | null): number =>
  fileSizeToBytes(file?.FileInfo?.Size ?? file?.SizeInBytes, file?.FileInfo?.Api);

export const sizeFileToInitials = (size: number) => {
  let initialIndex = 0;

  while (size / 1024 >= 1) {
    size = size / 1024;
    initialIndex++;
  }

  return `${size.toFixed(1)}${initials[initialIndex]}`;
};

export const sizeFileRangeToInitials = (
  currentSize: number, 
  totalSize: number,
  showSize = false,
) => {
  let initialIndex = 0;

  if (currentSize > totalSize) {
    currentSize = totalSize;
  }

  while (totalSize / 1024 >= 1) {
    totalSize /= 1024;
    currentSize /= 1024;
    initialIndex++;
  }

  if (showSize) {
    return `${currentSize.toFixed(1)}${initials[initialIndex]}/${totalSize.toFixed(1)}${initials[initialIndex]}`;    
  }

  return `${currentSize.toFixed(1)} / ${totalSize.toFixed(1)} ${initials[initialIndex]}`;
};

export function prop<T, K extends keyof T>(obj: T, key: K) {
  return obj[key];
}

export const msplit = (value: string, separators: string[]) => {
  return separators.reduce((segs, sep) => segs.reduce(
    (out, seg) => out.concat(seg.split(sep)), [] as string[]
  ), [value]).filter(x => x);
};

export const fakeBytesToMB = (size: number) => {
  const cutSize = size.toString().split('').slice(0, -5);
  const lastNumber = cutSize.pop();
  const formatSize = `${cutSize}.${lastNumber || 0}`;
  return parseFloat(formatSize);
}
